import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { ActionButton, ActionButtonType, Button, Dialog } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useAggregateAPIOperationStatus, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ModifyProjectTemplateVariableSetCommand, ModifyProjectTemplateSensitiveVariableSetCommand, ProjectTemplateVariableResource, ProjectTemplateVariableSetResource, Repository, VariableResource } from "@octopusdeploy/octopus-server-client";
import { Permission, VariableType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getVariableTypesChanged } from "~/areas/projects/components/Variables/ProjectVariables/projectVariableChanges";
import VariableEditor from "~/areas/variables/VariableEditor/VariableEditor";
import { createViewModel, getVariableResources } from "~/areas/variables/VariableEditor/conversions";
import type { VariableModel } from "~/areas/variables/VariablesModel/VariablesModel";
import groupVariablesByName from "~/areas/variables/groupVariablesByName";
import { repository } from "~/clientInstance";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { useNavigationBlocker } from "~/components/OctopusRouter/NavigationBlocker";
import { PageContent } from "~/components/PageContent/PageContent";
import { PermissionCheck } from "~/components/PermissionCheck";
import { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import type { ProjectTemplateContextProps } from "~/routing/pageRegistrations/ProjectTemplatePageRegistration";
import DateFormatter from "~/utils/DateFormatter/DateFormatter";
import IdHelper from "~/utils/IdHelper/index";
import { lastAccessedPlatformHubGitRef } from "../LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "../PlatformHubPageHeaderBranchSelector";
import { StopShareTemplateDialog } from "../StopShareTemplateDialog";
import createProjectTemplateOverflowMenuItems, { PlatformHubTemplateType } from "../platformHubHelpers";
import { ProjectTemplateCommitDialog } from "./ProjectTemplateCommitDialog";
import { GetProjectTemplateInitialSuccessCallout } from "./ProjectTemplateLayout";
import ProjectTemplatePublishButton from "./ProjectTemplatePublishButton";
import { ProjectTemplateWizardDialog, TemplateWizardSteps } from "./ProjectTemplateWizardDialog";
import { useProjectTemplateWizard, useIsProjectTemplateShared } from "./useProjectTemplateWizard";
import { useSaveProjectTemplate } from "./useSaveProjectTemplate";
import { useStopShareProjectTemplate } from "./useStopShareProjectTemplate";
const DEFAULT_COMMIT_MESSAGE = "Update variables";
/**
 * UI adapter type that adds properties required by VariableEditor component.
 * The backend ProjectTemplateVariableResource doesn't include these properties
 * to keep the API contract clean, but the UI components need them.
 * The VariableEditor component expects all variables to have IsEditable, Prompt, and IsSensitive
 * properties. Rather than forcing the backend to include properties it doesn't use, we adapt
 * the backend type at the boundary between data fetching and UI rendering.
 */
type ProjectTemplateVariableForUI = ProjectTemplateVariableResource & {
    IsEditable: boolean; // Always true - project template variables are always editable
    Prompt: null; // Always null - prompting happens during project creation, not in templates
    IsSensitive: boolean; // Derived from Type === 'Sensitive' for UI display purposes
};
/**
 * Adapts a backend ProjectTemplateVariableResource to the format expected by UI components.
 * - Adds IsEditable (always true for project template variables)
 * - Adds Prompt (always null - prompting happens during project creation, not in templates)
 * - Adds IsSensitive (derived from Type === 'Sensitive')
 * @param variable - The backend variable resource to adapt
 * @returns The adapted variable with UI-required properties
 */
function adaptToUIVariable(variable: ProjectTemplateVariableResource): ProjectTemplateVariableForUI {
    return {
        ...variable,
        IsEditable: true,
        Prompt: null,
        IsSensitive: variable.Type === "Sensitive",
    };
}
interface ProjectTemplateVariablesPageProps {
    loaderData: LoaderData;
    slug: string;
    gitRef: string;
    projectTemplateContext: ProjectTemplateContextProps;
}
type LoaderData = {
    variables: ProjectTemplateVariableSetResource;
    sensitiveVariables: ProjectTemplateVariableSetResource;
};
export async function projectTemplateVariablesPageLoader(repository: Repository, { routeParams }: {
    routeParams: {
        slug: string;
        gitRef: string;
    };
}): Promise<LoaderData> {
    const [variables, sensitiveVariables] = await Promise.all([getVariableSet(repository, routeParams.slug, routeParams.gitRef), getSensitiveVariableSet(repository, routeParams.slug)]);
    return {
        variables,
        sensitiveVariables,
    };
}
async function getVariableSet(repository: Repository, projectTemplateSlug: string, gitRef: string): Promise<ProjectTemplateVariableSetResource> {
    try {
        return await repository.ProjectTemplates.getVariableSet({
            ProjectTemplateSlug: projectTemplateSlug,
            GitRef: gitRef,
        });
    }
    catch (error) {
        // Handle missing variables file gracefully - return empty variables array
        if (error.ErrorMessage && error.ErrorMessage.match(/The file '\.octopus\/.*?\/variables\.ocl' on the '.*?' branch does not exist in the Git repository\./)) {
            logger.info("Variable set file not found, using empty set", error);
            return {
                ProjectTemplateSlug: projectTemplateSlug,
                Variables: [],
            };
        }
        throw error;
    }
}
async function getSensitiveVariableSet(repository: Repository, projectTemplateSlug: string): Promise<ProjectTemplateVariableSetResource> {
    try {
        return await repository.ProjectTemplates.getSensitiveVariableSet(projectTemplateSlug);
    }
    catch (error) {
        // Handle missing sensitive variables gracefully - return empty set with version 0
        logger.info("Sensitive variable set not found, using empty set", error);
        const emptySet: ProjectTemplateVariableSetResource = {
            ProjectTemplateSlug: projectTemplateSlug,
            Variables: [],
            Version: 0,
        };
        return emptySet;
    }
}
export function ProjectTemplateVariablesPage({ loaderData, slug, gitRef, projectTemplateContext }: ProjectTemplateVariablesPageProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { isInProgress } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const { openDialog, isOpen, closeAllDialogs } = useDialogTrigger();
    const navigation = useSpaceAwareNavigation();
    const [showInitialSuccessCallout, setShowInitialSuccessCallout] = useState(false);
    const { isOpen: isWizardOpen, openWizard, dialogProps } = useProjectTemplateWizard();
    const { isShared: isTemplateShared, refetchIsShared: refetchIsTemplateShared } = useIsProjectTemplateShared(slug);
    const { openDialog: openStopShareDialog, isOpen: isStopShareDialogOpen, closeDialog: closeStopShareDialog } = useDialogTrigger();
    const { projectTemplate, persistenceSettings, projectTemplateLink, publishedVersions, refetchPublishedVersions } = projectTemplateContext;
    const { handleStopShare } = useStopShareProjectTemplate(projectTemplate, refetchIsTemplateShared);
    const { executeSave } = useSaveProjectTemplate({
        projectTemplate,
        gitRef,
        commitHandler: dialogProps.commitHandler,
        onClose: dialogProps.onClose,
        refetchPublishedVersions,
        refetchIsTemplateShared,
        setShowInitialSuccessCallout,
    });
    const navigateToGitRef = useCallback((newGitRef: string) => {
        const routeParams = { slug, gitRef: newGitRef };
        navigation.redirect(links.projectTemplateVariablesPage.generateUrl(routeParams, undefined));
        lastAccessedPlatformHubGitRef.save(newGitRef, persistenceSettings);
    }, [navigation, slug, persistenceSettings]);
    const setGitRef = navigateToGitRef;
    const createStateFromVariables = useCallback((textVars: ProjectTemplateVariableSetResource, sensitiveVars: ProjectTemplateVariableSetResource) => {
        const allVariables = [...textVars.Variables, ...sensitiveVars.Variables];
        const adaptedVariables = allVariables.map(adaptToUIVariable);
        const groupedResources = groupVariablesByName(adaptedVariables, (v) => v.Name);
        const variables = createViewModel(groupedResources);
        return {
            textVariables: textVars,
            sensitiveVariables: sensitiveVars,
            groupedVariableResources: groupedResources,
            initialVariables: [...variables],
            editedVariables: variables,
            cellFocusResetKey: DateFormatter.timestamp(),
            sensitiveVariableSetVersion: sensitiveVars.Version ?? 0,
            advancedChangeDetection: { sensitiveChanged: false, textChanged: false },
        };
    }, []);
    const { result: { variables, sensitiveVariables }, refetch: refetchVariables, } = useQuery(async (repository, abortSignal) => {
        const [queryVariables, querySensitiveVariables] = await Promise.all([getVariableSet(repository, slug, gitRef), getSensitiveVariableSet(repository, slug)]);
        return { variables: queryVariables, sensitiveVariables: querySensitiveVariables };
    }, [slug, gitRef], "Get project template variable set", {
        initialResult: { variables: loaderData.variables, sensitiveVariables: loaderData.sensitiveVariables },
    });
    const [state, setState] = useState(() => createStateFromVariables(variables, sensitiveVariables));
    useEffect(() => {
        setState(createStateFromVariables(variables, sensitiveVariables));
    }, [variables, sensitiveVariables, createStateFromVariables]);
    const { textVariables, groupedVariableResources, initialVariables, editedVariables, cellFocusResetKey, sensitiveVariableSetVersion, advancedChangeDetection } = state;
    const hasChanges = React.useMemo(() => advancedChangeDetection.sensitiveChanged || advancedChangeDetection.textChanged, [advancedChangeDetection]);
    const scopeValues = useMemo(() => createEmptyScopeValues(), []);
    const handleSaveSensitiveOnly = useCallback(async (): Promise<void> => {
        const variables: VariableResource[] = getVariableResources(editedVariables, groupedVariableResources);
        const sensitiveVariablesFiltered = variables.filter((v) => v.Type === VariableType.Sensitive);
        createNewIdForUpdatesToSensitive(sensitiveVariablesFiltered, initialVariables);
        const sensitiveCommand: ModifyProjectTemplateSensitiveVariableSetCommand = {
            ProjectTemplateSlug: slug,
            Variables: sensitiveVariablesFiltered.map(toProjectTemplateVariable),
            Version: sensitiveVariableSetVersion,
        };
        await repository.ProjectTemplates.modifySensitiveVariableSet(sensitiveCommand);
        refetchVariables();
    }, [editedVariables, groupedVariableResources, initialVariables, sensitiveVariableSetVersion, slug, refetchVariables]);
    const handleCommit = useCallback(async (command: ModifyProjectTemplateVariableSetCommand): Promise<void> => {
        const variables: VariableResource[] = getVariableResources(editedVariables, groupedVariableResources);
        const textVariablesFiltered = variables.filter((v) => v.Type !== VariableType.Sensitive);
        const sensitiveVariablesFiltered = variables.filter((v) => v.Type === VariableType.Sensitive);
        createNewIdForUpdatesToSensitive(sensitiveVariablesFiltered, initialVariables);
        const { sensitiveChanged, textChanged } = getVariableTypesChanged(editedVariables, initialVariables);
        // Save text variables to Git
        if (textChanged) {
            const textCommand: ModifyProjectTemplateVariableSetCommand = {
                ...command,
                Variables: textVariablesFiltered.map(toProjectTemplateVariable),
            };
            await repository.ProjectTemplates.modifyVariableSet(textCommand);
        }
        // Save sensitive variables to database
        if (sensitiveChanged) {
            const sensitiveCommand: ModifyProjectTemplateSensitiveVariableSetCommand = {
                ProjectTemplateSlug: slug,
                Variables: sensitiveVariablesFiltered.map(toProjectTemplateVariable),
                Version: sensitiveVariableSetVersion,
            };
            await repository.ProjectTemplates.modifySensitiveVariableSet(sensitiveCommand);
        }
        refetchVariables();
    }, [editedVariables, groupedVariableResources, initialVariables, sensitiveVariableSetVersion, slug, refetchVariables]);
    const getCommandToCommit = useCallback((): ModifyProjectTemplateVariableSetCommand => {
        const variables: VariableResource[] = getVariableResources(editedVariables, groupedVariableResources);
        const projectTemplateVariables: ProjectTemplateVariableResource[] = variables.map((v) => ({
            Id: v.Id,
            Name: v.Name,
            Value: v.Value,
            Description: v.Description,
            Scope: v.Scope,
            Type: v.Type,
        }));
        return {
            ProjectTemplateSlug: slug,
            GitRef: gitRef,
            Variables: projectTemplateVariables,
        };
    }, [editedVariables, groupedVariableResources, slug, gitRef]);
    const createCommitHandler = useCallback(() => async (commitMessage: string) => {
        const command: ModifyProjectTemplateVariableSetCommand = {
            ...getCommandToCommit(),
            ChangeDescription: commitMessage,
        };
        await handleCommit(command);
    }, [getCommandToCommit, handleCommit]);
    const handleVariablesChanged = useCallback((variables: ReadonlyArray<VariableModel>) => {
        const { sensitiveChanged, textChanged } = getVariableTypesChanged(variables, initialVariables);
        setState((prev) => ({
            ...prev,
            editedVariables: variables,
            advancedChangeDetection: { sensitiveChanged, textChanged },
        }));
    }, [initialVariables]);
    const overflowActions: SimpleMenuItem[] = useMemo(() => createProjectTemplateOverflowMenuItems({
        projectTemplateLink,
        onShare: () => openWizard([TemplateWizardSteps.Share]),
        isShared: isTemplateShared,
        onStopShare: openStopShareDialog,
        additionalItems: [
            {
                type: "download-link",
                label: "Download as JSON",
                href: repository.ProjectTemplates.getVariableSetDownloadUrl(slug, gitRef),
                downloadFileName: `${slug}-variables.json`,
            },
        ],
    }), [gitRef, slug, projectTemplateLink, isTemplateShared, openStopShareDialog, openWizard]);
    const handleSaveOrCommit = useCallback(async () => {
        const { sensitiveChanged, textChanged } = advancedChangeDetection;
        // If only sensitive changed, save directly without dialog
        if (sensitiveChanged && !textChanged) {
            await doBusyTask(handleSaveSensitiveOnly);
        }
        else {
            // Show wizard for text changes or both
            if (isTemplateShared) {
                openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish], undefined, createCommitHandler());
            }
            else {
                openWizard([TemplateWizardSteps.Commit, TemplateWizardSteps.Publish, TemplateWizardSteps.Share], undefined, createCommitHandler());
            }
        }
    }, [advancedChangeDetection, doBusyTask, handleSaveSensitiveOnly, isTemplateShared, openWizard, createCommitHandler]);
    const handleQuickCommit = useCallback(async (): Promise<void> => {
        const { sensitiveChanged, textChanged } = advancedChangeDetection;
        // If only sensitive changed, save directly to database
        if (sensitiveChanged && !textChanged) {
            await doBusyTask(handleSaveSensitiveOnly);
        }
        else {
            // For text changes or both, use default commit message
            const command: ModifyProjectTemplateVariableSetCommand = {
                ...getCommandToCommit(),
                ChangeDescription: DEFAULT_COMMIT_MESSAGE, // Default message for quick commit
            };
            await doBusyTask(() => handleCommit(command));
        }
    }, [advancedChangeDetection, doBusyTask, handleSaveSensitiveOnly, handleCommit, getCommandToCommit]);
    const publishButton: PageAction = {
        type: "custom",
        content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                <ProjectTemplatePublishButton onPublish={() => openWizard([TemplateWizardSteps.Publish])}/>
            </PermissionCheck>),
        key: "Publish",
    };
    const primaryAction: PrimaryPageAction = useMemo(() => {
        const { sensitiveChanged, textChanged } = advancedChangeDetection;
        // Determine button label based on what changed
        const buttonLabel = sensitiveChanged && textChanged ? "Save & Commit" : sensitiveChanged ? "Save" : "Commit";
        return {
            type: "custom",
            content: (<PermissionCheck permission={Permission.PlatformHubEdit}>
                    <div role="group" className={styles.commitButtonContainerStyles}>
                        <ActionButton label={buttonLabel} type={ActionButtonType.Primary} accessibleName={buttonLabel} onClick={handleSaveOrCommit} disabled={!hasChanges || isInProgress || !hasPermission(Permission.PlatformHubEdit)}/>
                        <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={"Quick commit"} disabled={!hasChanges || isInProgress || !hasPermission(Permission.PlatformHubEdit)} onClick={handleQuickCommit}/>
                    </div>
                </PermissionCheck>),
            key: "commit-actions",
        };
    }, [advancedChangeDetection, hasChanges, isInProgress, handleSaveOrCommit, handleQuickCommit]);
    useNavigationBlocker({
        when: hasChanges,
    });
    const callout = GetProjectTemplateInitialSuccessCallout({ showInitialSuccessCallout, setShowInitialSuccessCallout, projectTemplateName: projectTemplate.Name });
    return (<>
            <PageContent header={{
            title: "Variables",
            contextSelector: <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={persistenceSettings}/>,
            primaryAction,
            pageActions: [publishButton],
            overflowActions,
        }} busy={isInProgress} callout={callout}>
                <VariableEditor initialVariables={initialVariables} scopeValues={scopeValues} isTenanted={false} doBusyTask={doBusyTask} onVariablesChanged={handleVariablesChanged} cellFocusResetKey={cellFocusResetKey} scope="PlatformHub" gitVariables={true} ephemeralEnvironmentsAreEnabled={false}/>
            </PageContent>

            <ProjectTemplateCommitDialog open={isOpen} gitRef={gitRef} onClose={closeAllDialogs} onCommit={handleCommit} getCommandToCommit={getCommandToCommit} defaultCommitMessage={DEFAULT_COMMIT_MESSAGE} entityName="project template"/>

            {isWizardOpen && (<ProjectTemplateWizardDialog projectTemplate={projectTemplate} gitRef={gitRef} defaultBranch={persistenceSettings.DefaultBranch} stepsToShow={dialogProps.stepsToShow} getDefaultCommitMessage={() => DEFAULT_COMMIT_MESSAGE} onClose={dialogProps.onClose} onInitialSuccessCallout={() => setShowInitialSuccessCallout(true)} publishedVersions={publishedVersions} executeSave={executeSave}/>)}

            <Dialog open={isStopShareDialogOpen}>
                <StopShareTemplateDialog onSave={handleStopShare} onClose={closeStopShareDialog} templateType={PlatformHubTemplateType.ProjectTemplate}/>
            </Dialog>
        </>);
}
const styles = {
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
function toProjectTemplateVariable(v: VariableResource): ProjectTemplateVariableResource {
    return {
        Id: v.Id,
        Name: v.Name,
        Value: v.Value,
        Description: v.Description,
        Scope: v.Scope,
        Type: v.Type,
    };
}
function createEmptyScopeValues() {
    return {
        Actions: [],
        Channels: [],
        Environments: [],
        Machines: [],
        Roles: [],
        TenantTags: [],
        Processes: [],
    };
}
function createNewIdForUpdatesToSensitive(sensitiveVariables: VariableResource[], initialVariables: ReadonlyArray<VariableModel>) {
    // Create new ID for variables that have been changed to sensitive so that they can be saved to the server without conflicts.
    // See https://app.shortcut.com/octopusdeploy/story/75424/conflicts-with-cac-variables-and-sensitive-variables-requested-by-finnian-dempsey
    // and https://github.com/OctopusDeploy/Issues/issues/8733
    const initialVariableIdsAndType: Record<string, string> = {};
    initialVariables.forEach((variable) => {
        variable.values.forEach((value) => {
            initialVariableIdsAndType[value.Id] = value.Type;
        });
    });
    sensitiveVariables.forEach((variable) => {
        if (initialVariableIdsAndType[variable.Id] !== undefined && initialVariableIdsAndType[variable.Id] !== VariableType.Sensitive) {
            variable.Id = IdHelper.newId();
        }
    });
}
